import { useContext, useEffect, useRef } from 'react';
import { AuthContext } from '@/stores/AuthContext';
import { Role } from '@/types/auth';

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  // "Autenticado" pro lado staff = Firebase user + profile populado.
  // Um paciente logado no /portal tem `user` mas `profile=null` (ver AuthContext)
  // e não deve conseguir entrar nas rotas internas da clínica.
  const isAuth = !!context.user && !!context.profile;

  // Tempo de sessao configuravel pela clinica. 0 = desabilitado (nunca desloga).
  // TOTEM nunca expira: o dispositivo fica ligado o dia todo na recepção e a
  // sessão precisa sobreviver entre atendimentos sem reautenticação.
  const isTotem = context.profile?.role === Role.TOTEM;
  const timeoutMs = isTotem ? 0 : context.sessionTimeoutMinutes * 60 * 1000;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAuth || timeoutMs <= 0) return;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        context.logout();
      }, timeoutMs);
    };

    // `dratento:atividade`: fala no ditado ao vivo conta como uso — ditando
    // sem tocar no teclado, o médico não pode ser deslogado no meio.
    const events = ['mousemove', 'keydown', 'scroll', 'click', 'dratento:atividade'];
    events.forEach(e => window.addEventListener(e, resetTimer));

    resetTimer(); // init

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [isAuth, timeoutMs, context.logout]);

  return {
    user: context.user,
    profile: context.profile,
    loading: context.loading,
    loginWithGoogle: context.loginWithGoogle,
    logout: context.logout,
    isAuthenticated: isAuth,
    role: context.profile?.role,
    permissions: context.profile?.permissions,
    clinicId: context.profile?.clinicId
  };
}
